import { CostPriceIngredient, toIngredientUpdatePayload } from '../services/costPrice/costPriceService';
import { productKey } from './reports/shared';

export type CostPriceCopySelection = 'ingredients' | 'subrecipes' | 'both';
type ClipboardIngredient = Omit<CostPriceIngredient, 'id' | 'currentPrice' | 'currentPriceDate'>;

export interface CostPriceClipboard {
  restaurantId: string;
  sourceName: string;
  ingredients: ClipboardIngredient[];
}

export function copyCostPriceIngredients(rows: CostPriceIngredient[], selection: CostPriceCopySelection): ClipboardIngredient[] {
  return structuredClone(rows
    .filter(row => !row.nestedCostPriceId && (selection === 'both' || (selection === 'subrecipes' ? !!row.subrecipeId : !row.subrecipeId)))
    .map(row => {
      const { automationTemplateKey, ...ingredient } = toIngredientUpdatePayload(row);
      // A copied line is not part of the source dish's automation template.
      return ingredient;
    }));
}

function ingredientIdentity(row: ClipboardIngredient, restaurantId: string): string {
  const source = row.sourceRestaurantId || restaurantId;
  if (row.subrecipeId) return JSON.stringify([source, 'subrecipe', row.subrecipeId]);
  if (row.nestedCostPriceId) return JSON.stringify([source, 'nested', row.nestedCostPriceId]);
  // Custom library rows use their article id even on no-article-id platforms.
  const identity = row.custom
    ? [row.platform?.toLowerCase(), row.articleId || [row.productName?.toLowerCase(), row.quantityInfo?.stdUnit]]
    : productKey(row.platform ?? '', row.articleId, row.productName);
  return JSON.stringify([source, row.custom ? 'custom' : 'order', identity]);
}

/** Append only new identities; keep every destination row and quantity intact. */
export function mergeCostPriceClipboard(current: CostPriceIngredient[], clipboard: CostPriceClipboard, restaurantId: string) {
  const ingredients = current.map(toIngredientUpdatePayload);
  if (clipboard.restaurantId !== restaurantId) return { ingredients, added: 0, skipped: clipboard.ingredients.length };
  const seen = new Set(ingredients.map(row => ingredientIdentity(row, restaurantId)));
  let added = 0;
  for (const row of clipboard.ingredients) {
    if (row.nestedCostPriceId) continue;
    const key = ingredientIdentity(row, restaurantId);
    if (seen.has(key)) continue;
    seen.add(key);
    ingredients.push(structuredClone(row));
    added++;
  }
  return { ingredients, added, skipped: clipboard.ingredients.length - added };
}
